import { useLogger } from "@sg-protect/federation-runtime/squide";
import { type ComponentType, type ErrorInfo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router";

export interface ProtectHeaderWidgetOptions {
    id: string;
    component: ComponentType;
}

// Not reactive: read feature flags inside the widget, since a widget registered or skipped here never changes.
const widgets = new Map<string, ProtectHeaderWidgetOptions>();

export function registerProtectHeaderWidget(options: ProtectHeaderWidgetOptions) {
    widgets.set(options.id, options);

    return () => {
        widgets.delete(options.id);
    };
}

export interface HeaderWidgetsProps {
    widgets: ProtectHeaderWidgetOptions[];
}

// Other modules inject these, so a crash in one must not blank the header.
export function HeaderWidgets({ widgets: headerWidgets }: HeaderWidgetsProps) {
    const logger = useLogger();
    const { pathname } = useLocation();

    const handleError = (id: string, error: unknown, info: ErrorInfo) => {
        logger
            .withText(`[shell] The "${id}" header widget crashed and was hidden`)
            .withError(error instanceof Error ? error : new Error(String(error)))
            .withObject(info)
            .error();
    };

    return headerWidgets.map(({ id, component: Component }) => (
        <ErrorBoundary
            key={id}
            fallbackRender={() => null}
            onError={(error, info) => handleError(id, error, info)}
            resetKeys={[pathname]}
        >
            <Component />
        </ErrorBoundary>
    ));
}

export function RegisteredHeaderWidgets() {
    return <HeaderWidgets widgets={Array.from(widgets.values())} />;
}
